import { useState } from 'react';
import type { ComponentType } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/auth';
import { api } from '../api/client';
import { adminUrl } from '../lib/appUrls';
import { LanguageSwitch } from './LanguageSwitch';
import { ThemeToggle } from './ThemeToggle';
import { Button } from './ui';
import {
  IconDashboard,
  IconArchive,
  IconUpload,
  IconCloud,
  IconSearch,
  IconVersions,
  IconRetention,
  IconRetrieval,
  IconAudit,
  IconBilling,
  IconBell,
  IconLogout,
  IconMenu,
  IconWarning,
} from './Icons';

export interface NavEntry {
  to: string;
  key: string;
  Icon: ComponentType<{ width?: number; height?: number }>;
}

// Order is mandated by MASTER_SPEC §22 / task brief.
const NAV: NavEntry[] = [
  { to: '/', key: 'dashboard', Icon: IconDashboard },
  { to: '/archives', key: 'archives', Icon: IconArchive },
  { to: '/upload', key: 'upload', Icon: IconUpload },
  { to: '/s3import', key: 's3import', Icon: IconCloud },
  { to: '/search', key: 'search', Icon: IconSearch },
  { to: '/versions', key: 'versions', Icon: IconVersions },
  { to: '/retention', key: 'retention', Icon: IconRetention },
  { to: '/retrieval', key: 'retrieval', Icon: IconRetrieval },
  { to: '/audit', key: 'audit', Icon: IconAudit },
  { to: '/billing', key: 'billing', Icon: IconBilling },
  { to: '/notifications', key: 'notifications', Icon: IconBell },
];

/**
 * Shared application chrome. The customer portal and the admin console are
 * built as SEPARATE bundles/containers (deploy/charts/portal-web and
 * deploy/charts/admin-web), so the nav is injected rather than branched on a
 * role here — that keeps admin routes out of the customer bundle entirely.
 */
export function AppShell({
  nav = NAV,
  sectionKey = 'nav.sectionMain',
  brandTagKey = 'app.tagline',
}: {
  nav?: NavEntry[];
  sectionKey?: string;
  brandTagKey?: string;
} = {}) {
  const { t } = useTranslation();
  const { me } = useAuth();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  // Pick the most specific (longest) matching entry, not the first one found —
  // otherwise a bare "/" or "/admin" entry would shadow its own children.
  const pageTitle = (() => {
    let best: NavEntry | undefined;
    for (const n of nav) {
      const isMatch = n.to === '/' ? location.pathname === '/' : location.pathname === n.to || location.pathname.startsWith(n.to + '/');
      if (isMatch && (!best || n.to.length > best.to.length)) best = n;
    }
    return best ? t(`nav.${best.key}`) : t('app.name');
  })();

  return (
    <div className="shell">
      <a className="skip-link" href="#main">
        {t('app.skipToContent')}
      </a>

      <div className={`scrim${open ? ' is-open' : ''}`} onClick={() => setOpen(false)} aria-hidden="true" />

      <aside className={`sidebar${open ? ' is-open' : ''}`} aria-label={t(sectionKey)}>
        <div className="brand">
          <span className="brand__mark" aria-hidden="true">
            e
          </span>
          <span>
            <span className="brand__name">{t('app.name')}</span>
            <br />
            <span className="brand__tag">{t(brandTagKey)}</span>
          </span>
        </div>
        <nav className="nav" aria-label={t(sectionKey)}>
          <div className="nav__label">{t(sectionKey)}</div>
          <ul className="nav__list">
            {nav.map(({ to, key, Icon }) => (
              <li key={key}>
                <NavLink
                  to={to}
                  end={to === '/'}
                  className={({ isActive }) => `nav__link${isActive ? ' is-active' : ''}`}
                  onClick={() => setOpen(false)}
                >
                  <span className="nav__icon">
                    <Icon width={18} height={18} />
                  </span>
                  {t(`nav.${key}`)}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
      </aside>

      <div className="content">
        {me.impersonating ? (
          <div
            className="row between"
            role="status"
            style={{ background: 'var(--warn-bg, #fef3c7)', color: 'var(--warn-fg, #92400e)', padding: '8px 16px', fontSize: 13 }}
          >
            <span className="row" style={{ gap: 6 }}>
              <IconWarning width={16} height={16} />
              {t('admin.impersonation.banner', { tenant: me.tenantName })}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={async () => {
                await api.adminExitImpersonation();
                // Back to the admin console — a different host in the split
                // deployment, so a full navigation rather than a router push.
                window.location.href = adminUrl('/');
              }}
            >
              {t('admin.impersonation.exit')}
            </Button>
          </div>
        ) : null}
        <header className="topbar">
          <button
            className="icon-btn menu-btn"
            onClick={() => setOpen((o) => !o)}
            aria-label={open ? t('app.closeMenu') : t('app.openMenu')}
            aria-expanded={open}
          >
            <IconMenu width={18} height={18} />
          </button>
          <span className="topbar__title">{pageTitle}</span>
          <div className="topbar__spacer" />
          <div className="topbar__meta">
            <span className="tenant-chip" title={`${t('app.tenant')}: ${me.tenantName}`}>
              <span className="tenant-chip__dot" aria-hidden="true" />
              <span className="visually-hidden">{t('app.tenant')}: </span>
              {me.tenantName}
            </span>
            <LanguageSwitch />
            <ThemeToggle />
            <span className="row" style={{ gap: 6, paddingLeft: 4 }}>
              <span style={{ textAlign: 'right', lineHeight: 1.2 }}>
                <strong style={{ fontSize: 13 }}>{me.username}</strong>
                <br />
                <span className="brand__tag">
                  {me.role === 'admin' ? t('app.roleAdmin') : t('app.roleUser')}
                </span>
              </span>
            </span>
            <form method="POST" action="/logout">
              <Button variant="ghost" type="submit" title={t('app.logout')} aria-label={t('app.logout')}>
                <IconLogout width={18} height={18} />
              </Button>
            </form>
          </div>
        </header>

        <main className="main" id="main" tabIndex={-1}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
